/**
 * QuickGlassLossModal — szybkie dodanie strat szkła z ekranu logowania.
 * Wspiera wiele pozycji w jednym zgłoszeniu + wyszukiwarkę po nazwie.
 *
 * Flow:
 *  1. Jeśli `glassPattern` istnieje → najpierw PatternLock.
 *  2. Po odblokowaniu → wyszukiwarka + lista typów szkła, każdy z input ilości i przyciskiem "Dodaj".
 *  3. Koszyk pokazuje wybrane pozycje (z możliwością usunięcia).
 *  4. Zapis → tworzy N ruchów typu 'loss' (POST /api/glass/movements), success + close.
 */
import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import PatternLock from './PatternLock';
import PortalOverlay from './common/PortalOverlay';

const API = process.env.REACT_APP_BACKEND_URL;

const QuickGlassLossModal = ({ isOpen, onClose, location, user }) => {
  const [glassTypes, setGlassTypes] = useState([]);
  const [pattern, setPattern] = useState(null);
  const [loadingPattern, setLoadingPattern] = useState(true);
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [success, setSuccess] = useState(false);

  // Form
  const [search, setSearch] = useState('');
  const [qtyDraft, setQtyDraft] = useState({}); // { [glassTypeId]: '3' }
  const [cart, setCart] = useState([]); // [{ id, glassTypeId, name, qty }]
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setIsUnlocked(false);
    setSuccess(false);
    setSearch('');
    setQtyDraft({});
    setCart([]);
    setNote('');

    (async () => {
      try {
        setLoadingPattern(true);
        const r = await axios.get(`${API}/api/settings/glass-pattern?location=${location}`);
        setPattern(r.data?.pattern || null);
      } catch {
        setPattern(null);
      } finally {
        setLoadingPattern(false);
      }
    })();

    (async () => {
      try {
        const r = await axios.get(`${API}/api/glass/types?location=${location}`);
        setGlassTypes(r.data || []);
      } catch (e) {
        console.error('glass types', e);
        setGlassTypes([]);
      }
    })();
  }, [isOpen, location]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return []; // Brak listy gdy puste — user musi najpierw wyszukać
    return glassTypes
      .filter((g) => (g.name || '').toLowerCase().includes(q))
      .slice(0, 15); // max 15 wyników żeby nie zalewać UI
  }, [glassTypes, search]);

  const addToCart = (g) => {
    const qtyN = Number(qtyDraft[g.id] || 0);
    if (!qtyN || qtyN <= 0) {
      toast.error(`Podaj ilość dla "${g.name}"`);
      return;
    }
    setCart((prev) => [
      ...prev,
      { id: `${g.id}-${Date.now()}`, glassTypeId: g.id, name: g.name, qty: qtyN },
    ]);
    setQtyDraft((p) => ({ ...p, [g.id]: '' }));
    setSearch(''); // Po dodaniu czyść wyszukiwarkę = przygotowane do kolejnego
    toast.success(`Dodano: ${g.name} × ${qtyN}`);
  };

  const removeFromCart = (cid) => {
    setCart((p) => p.filter((x) => x.id !== cid));
  };

  const totalQty = cart.reduce((s, x) => s + x.qty, 0);

  const handleSubmit = async () => {
    if (cart.length === 0) {
      toast.error('Dodaj przynajmniej jedną pozycję');
      return;
    }
    setSubmitting(true);
    try {
      const today = new Date().toISOString().slice(0, 10);
      await Promise.all(
        cart.map((item) =>
          axios.post(`${API}/api/glass/movements`, {
            location,
            type: 'loss',
            glassTypeId: item.glassTypeId,
            qty: item.qty,
            date: today,
            note: note.trim() || null,
            addedBy: user?.username || 'quick',
          })
        )
      );
      toast.success(`💥 Zapisano ${cart.length} ${cart.length === 1 ? 'pozycję' : 'pozycji'} (${totalQty} szt.)`);
      setSuccess(true);
      setTimeout(() => onClose(), 1800);
    } catch (e) {
      toast.error('Błąd: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  if (pattern && !isUnlocked && !success && !loadingPattern) {
    return (
      <PatternLock
        mode="verify"
        currentPattern={pattern}
        onComplete={() => setIsUnlocked(true)}
        onCancel={onClose}
      />
    );
  }

  if (success) {
    return (
      <PortalOverlay onClose={onClose}>
      <div
        data-testid="quick-glass-loss-success"
        style={{
          position: 'fixed', inset: 0, zIndex: 99999,
          background: 'rgba(0,0,0,0.92)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        <div style={{
          background: 'linear-gradient(135deg, rgba(34,197,94,0.18), rgba(16,185,129,0.10))',
          border: '2px solid rgba(34,197,94,0.55)',
          borderRadius: 18, padding: '36px 48px', textAlign: 'center',
        }}>
          <div style={{ fontSize: 56, marginBottom: 10 }}>✅</div>
          <div style={{ fontSize: 20, fontWeight: 800, color: '#fff', marginBottom: 6 }}>Zapisano</div>
          <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.7)' }}>
            {cart.length} {cart.length === 1 ? 'pozycja' : 'pozycji'} · łącznie {totalQty} szt.
          </div>
        </div>
      </div>
      </PortalOverlay>
    );
  }

  return (
    <PortalOverlay onClose={onClose}>
    <div
      data-testid="quick-glass-loss-modal"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 99999,
        background: 'rgba(0,0,0,0.85)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16, animation: 'qglFade .2s ease',
      }}
    >
      <style>{`@keyframes qglFade { from {opacity:0} to {opacity:1} }`}</style>
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 520, maxHeight: '90vh',
          background: 'linear-gradient(135deg, #161619, #0f0f10)',
          border: '1px solid rgba(255,255,255,0.08)',
          borderRadius: 18,
          padding: 22, position: 'relative',
          boxShadow: '0 24px 64px rgba(0,0,0,0.6)',
          display: 'flex', flexDirection: 'column',
        }}
      >
        {/* Close */}
        <button
          type="button"
          onClick={onClose}
          data-testid="quick-glass-loss-close"
          aria-label="Zamknij"
          style={{
            position: 'absolute', top: 12, right: 12,
            width: 36, height: 36, borderRadius: '50%',
            background: 'rgba(239,68,68,0.15)', color: '#ef4444',
            border: '1px solid rgba(239,68,68,0.35)', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 18, fontWeight: 800, lineHeight: 1,
          }}
        >
          ✕
        </button>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
          <div style={{
            width: 46, height: 46, borderRadius: 12,
            background: 'linear-gradient(135deg, #ef4444, #b91c1c)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 22, boxShadow: '0 6px 18px rgba(239,68,68,0.45)',
          }}>💥</div>
          <div>
            <div style={{ fontWeight: 800, fontSize: 17, color: '#fafafa' }}>Szybka strata szkła</div>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.55)' }}>Dodaj wiele pozycji naraz</div>
          </div>
        </div>

        {/* Search */}
        <div style={{ position: 'relative', marginBottom: 10 }}>
          <span style={{
            position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)',
            fontSize: 14, color: 'rgba(255,255,255,0.5)', pointerEvents: 'none',
          }}>🔍</span>
          <input
            type="text"
            placeholder="Szukaj po nazwie szkła…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="qgl-search"
            style={{ ...inputStyle, paddingLeft: 36 }}
          />
        </div>

        {/* Lista typów szkła (dropdown wyników) — pokazuje się TYLKO gdy wpisano coś w wyszukiwarce */}
        {search.trim() && (
          <div
            data-testid="qgl-glass-list"
            style={{
              overflowY: 'auto', maxHeight: '36vh',
              border: '1px solid rgba(255,255,255,0.06)',
              borderRadius: 10, padding: 6, marginBottom: 12,
              background: 'rgba(255,255,255,0.02)',
            }}
          >
            {filtered.length === 0 ? (
              <div style={{ padding: 16, textAlign: 'center', fontSize: 13, color: 'rgba(255,255,255,0.45)' }}>
                Brak wyników dla &bdquo;{search}&rdquo;
              </div>
            ) : (
              filtered.map((g) => (
                <div
                  key={g.id}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    padding: '8px 10px', borderRadius: 8,
                    borderBottom: '1px solid rgba(255,255,255,0.04)',
                  }}
                >
                  <div style={{ flex: 1, fontSize: 13, fontWeight: 600, color: '#fafafa' }}>{g.name}</div>
                  <input
                    type="number"
                    min="1" step="1"
                    placeholder="szt."
                    value={qtyDraft[g.id] || ''}
                    onChange={(e) => setQtyDraft((p) => ({ ...p, [g.id]: e.target.value }))}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addToCart(g); } }}
                    data-testid={`qgl-qty-${g.id}`}
                    style={{ ...inputStyle, width: 70, padding: '6px 8px', textAlign: 'center' }}
                  />
                  <button
                    type="button"
                    onClick={() => addToCart(g)}
                    data-testid={`qgl-add-${g.id}`}
                    title="Dodaj do listy"
                    style={{
                      width: 32, height: 32, borderRadius: 8,
                      background: 'linear-gradient(135deg, #ef4444, #b91c1c)',
                      color: '#fff', border: 'none', cursor: 'pointer',
                      fontSize: 18, fontWeight: 800, lineHeight: 1,
                    }}
                  >+</button>
                </div>
              ))
            )}
          </div>
        )}

        {/* Hint gdy puste pole wyszukiwania */}
        {!search.trim() && cart.length === 0 && (
          <div style={{
            padding: '12px 14px', marginBottom: 12,
            background: 'rgba(255,255,255,0.03)',
            border: '1px dashed rgba(255,255,255,0.10)',
            borderRadius: 10, fontSize: 12, color: 'rgba(255,255,255,0.55)',
            textAlign: 'center',
          }}>
            🔎 Zacznij wpisywać nazwę szkła, żeby wyszukać
          </div>
        )}

        {/* Koszyk wybranych pozycji */}
        {cart.length > 0 && (
          <div
            data-testid="qgl-cart"
            style={{
              marginBottom: 12, padding: 10,
              background: 'rgba(239,68,68,0.06)',
              border: '1px solid rgba(239,68,68,0.25)',
              borderRadius: 10,
            }}
          >
            <div style={{ fontSize: 11, fontWeight: 800, color: '#fca5a5', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>
              Do zapisania ({cart.length} · {totalQty} szt.)
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {cart.map((item) => (
                <div
                  key={item.id}
                  style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}
                >
                  <span style={{ flex: 1, color: '#fafafa' }}>{item.name}</span>
                  <span style={{ color: '#fca5a5', fontWeight: 700 }}>× {item.qty}</span>
                  <button
                    type="button"
                    onClick={() => removeFromCart(item.id)}
                    data-testid={`qgl-remove-${item.id}`}
                    style={{
                      width: 22, height: 22, borderRadius: '50%',
                      background: 'rgba(239,68,68,0.2)', color: '#fca5a5',
                      border: '1px solid rgba(239,68,68,0.35)', cursor: 'pointer',
                      fontSize: 12, lineHeight: 1,
                    }}
                  >✕</button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Notatka */}
        <input
          type="text"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Notatka (opcjonalnie) — np. „Stłuczka przy zmywaniu"
          data-testid="qgl-note"
          style={{ ...inputStyle, marginBottom: 12 }}
        />

        {/* Submit */}
        <button
          type="button"
          onClick={handleSubmit}
          disabled={submitting || cart.length === 0}
          data-testid="qgl-submit"
          style={{
            width: '100%', padding: '12px 16px', borderRadius: 12,
            background: submitting || cart.length === 0
              ? 'rgba(255,255,255,0.08)'
              : 'linear-gradient(135deg, #ef4444, #b91c1c)',
            color: submitting || cart.length === 0 ? '#9ca3af' : '#fff',
            border: 'none', cursor: submitting ? 'wait' : (cart.length === 0 ? 'not-allowed' : 'pointer'),
            fontWeight: 800, fontSize: 14,
            boxShadow: submitting || cart.length === 0 ? 'none' : '0 8px 22px rgba(239,68,68,0.45)',
          }}
        >
          {submitting
            ? '⏳ Zapisuję…'
            : cart.length === 0
              ? 'Dodaj pozycje, żeby zapisać'
              : `💾 Zapisz straty (${cart.length})`}
        </button>
      </div>
    </div>
    </PortalOverlay>
  );
};

const inputStyle = {
  width: '100%', boxSizing: 'border-box',
  padding: '11px 13px', borderRadius: 10,
  background: 'rgba(255,255,255,0.04)',
  border: '1px solid rgba(255,255,255,0.08)',
  color: '#fafafa', fontSize: 14, outline: 'none',
};

export default QuickGlassLossModal;
